import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  KeyRound, Calendar as CalendarIcon, Video, X, RefreshCw, Ticket,
  ShieldCheck, Trash2, LogOut, AlertCircle, ArrowRight, Star,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useAuth } from '../context/AuthContext';
import api, { mensajeDeError, codigoRecordado, olvidarCodigo, pesos } from '../lib/api';
import { toast } from 'sonner';

/**
 * Portal del paciente.
 *
 * Se entra con el código de continuidad, nunca con correo ni contraseña.
 *
 * Lo primero que se ve es la próxima sesión con su cuenta regresiva: es la
 * razón por la que alguien entra acá. El resto —historial, bonos, borrar
 * datos— queda debajo, disponible pero sin competir por la atención.
 */
const MisHorasPage = () => {
  const { user, isPatient, recuperarIdentidad, logout, loading: autenticando } = useAuth();

  const [codigo, setCodigo] = useState('');
  const [reservas, setReservas] = useState([]);
  const [perfil, setPerfil] = useState(null);
  const [bonos, setBonos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [porCancelar, setPorCancelar] = useState(null);
  const [porBorrar, setPorBorrar] = useState(false);

  useEffect(() => {
    const guardado = codigoRecordado();
    if (guardado) setCodigo(guardado);
  }, []);

  const cargarDatos = useCallback(async () => {
    setCargando(true);

    // allSettled: que falle una parte no debe dejar la pantalla en blanco.
    const [r1, r2, r3] = await Promise.allSettled([
      api.get('/mi/reservas'),
      api.get('/mi/perfil'),
      api.get('/mi/bonos'),
    ]);

    if (r1.status === 'fulfilled') setReservas(r1.value.data);
    if (r2.status === 'fulfilled') setPerfil(r2.value.data);
    if (r3.status === 'fulfilled') setBonos(r3.value.data);

    const expiro = [r1, r2, r3].some((r) => r.status === 'rejected' && r.reason?.response?.status === 401);
    if (expiro) {
      logout();
      toast.error('Tu sesión expiró. Ingresa tu código otra vez.');
    }

    setCargando(false);
  }, [logout]);

  useEffect(() => {
    if (isPatient) cargarDatos();
  }, [isPatient, cargarDatos]);

  const entrar = async (evento) => {
    evento.preventDefault();
    const resultado = await recuperarIdentidad(codigo);
    if (!resultado.ok) {
      toast.error(resultado.error);
      return;
    }
    toast.success('Bienvenido de vuelta');
  };

  const cancelar = async () => {
    const reserva = porCancelar;
    setPorCancelar(null);
    try {
      await api.patch(`/reservas/${reserva.id}/cancel`);
      toast.success('Tu hora fue cancelada');
      cargarDatos();
    } catch (err) {
      toast.error(mensajeDeError(err, 'No pudimos cancelar tu hora'));
    }
  };

  const borrarCuenta = async () => {
    setPorBorrar(false);
    try {
      const { data } = await api.delete('/mi/cuenta');
      olvidarCodigo();
      logout();
      toast.success(data.message);
    } catch (err) {
      toast.error(mensajeDeError(err, 'No pudimos borrar tu identidad'));
    }
  };

  // ---------- Pantalla de acceso ----------
  if (!isPatient) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4">
              <KeyRound className="w-7 h-7 text-teal-700" />
            </div>
            <h1 className="font-heading text-3xl font-semibold text-slate-900">Mis horas</h1>
            <p className="text-slate-600 mt-2">
              Ingresa tu código de continuidad. No necesitas correo ni contraseña.
            </p>
          </div>

          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <form onSubmit={entrar}>
                <Label htmlFor="codigo" className="text-slate-700">Código de continuidad</Label>
                <Input
                  id="codigo"
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value.toUpperCase())}
                  placeholder="ABCD-2345-EFGH"
                  autoComplete="off"
                  spellCheck="false"
                  className="mt-2 font-mono text-lg tracking-widest text-center"
                  data-testid="input-codigo"
                />

                <Button
                  type="submit"
                  disabled={autenticando || codigo.trim().length < 12}
                  className="w-full mt-5 bg-teal-600 hover:bg-teal-700 text-white rounded-full py-6"
                  data-testid="entrar-btn"
                >
                  {autenticando ? 'Buscando…' : 'Entrar'}
                  {!autenticando && <ArrowRight className="ml-2 w-4 h-4" />}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-slate-100 space-y-3 text-sm text-slate-600">
                <p className="flex gap-2">
                  <ShieldCheck className="w-4 h-4 text-teal-600 flex-none mt-0.5" />
                  Tu código es lo único que te identifica. No sabemos tu nombre.
                </p>
                <p className="flex gap-2">
                  <AlertCircle className="w-4 h-4 text-slate-400 flex-none mt-0.5" />
                  <span>
                    ¿Lo perdiste? No podemos recuperarlo, pero puedes{' '}
                    <Link to="/agendar" className="text-teal-700 underline">agendar de nuevo</Link>.
                  </span>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ---------- Portal ----------
  const proximas = reservas.filter((r) => r.is_upcoming);
  const pasadas = reservas.filter((r) => !r.is_upcoming);
  const siguiente = proximas[0];
  const bonosActivos = bonos.filter((b) => b.usable);
  const realizadas = pasadas.filter((r) => r.status === 'completed').length;

  return (
    <div className="container-max mx-auto px-4 md:px-8 py-10">
      {/* Saludo */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div className="min-w-0">
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900">
            Hola, {user?.pseudonym}
          </h1>
          {perfil && (
            <p className="text-slate-600 mt-1">
              {realizadas === 0
                ? 'Este es tu espacio. Acá aparecen tus horas.'
                : `Llevas ${realizadas} ${realizadas === 1 ? 'sesión realizada' : 'sesiones realizadas'}.`}
            </p>
          )}
        </div>

        <Button
          variant="ghost"
          onClick={() => {
            logout();
            setReservas([]);
            setPerfil(null);
            toast.success('Sesión cerrada');
          }}
          className="text-slate-500"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Salir
        </Button>
      </div>

      {/* Próxima sesión: lo que la persona vino a ver */}
      {siguiente ? (
        <ProximaSesion reserva={siguiente} onCancelar={() => setPorCancelar(siguiente)} />
      ) : (
        <Card className="border-dashed border-slate-300">
          <CardContent className="py-12 text-center">
            <CalendarIcon className="w-10 h-10 text-slate-300 mx-auto mb-4" />
            <p className="font-medium text-slate-900 mb-1">No tienes horas agendadas</p>
            <p className="text-slate-600 mb-6">
              {bonosActivos.length > 0
                ? 'Tienes sesiones disponibles en tu bono.'
                : 'Cuando quieras retomar, acá estamos.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to={bonosActivos.length > 0 ? `/agendar?profesional=${bonosActivos[0].psychologist_id}` : '/encuentra'}>
                <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8 w-full">
                  {bonosActivos.length > 0 ? 'Usar una sesión de mi bono' : 'Encontrar un psicólogo'}
                </Button>
              </Link>
              {bonosActivos.length === 0 && (
                <Link to="/agendar">
                  <Button variant="outline" className="rounded-full px-8 w-full">
                    Ver profesionales
                  </Button>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bonos */}
      {bonosActivos.length > 0 && (
        <section className="mt-8">
          <h2 className="font-heading text-lg font-semibold text-slate-900 mb-3">Tus bonos</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {bonosActivos.map((b) => (
              <Card key={b.id} className="border-teal-200 bg-teal-50/40">
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 truncate">{b.psychologist_name}</p>
                      <p className="text-sm text-slate-600">
                        {b.remaining} de {b.total_sessions} sesiones disponibles
                      </p>
                    </div>
                    <Ticket className="w-5 h-5 text-teal-600 flex-none" />
                  </div>

                  <div className="h-1.5 rounded-full bg-teal-100 overflow-hidden mb-3">
                    <div
                      className="h-full bg-teal-600"
                      style={{ width: `${(b.remaining / b.total_sessions) * 100}%` }}
                    />
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-2">
                    {b.expires_at && (
                      <span className="text-xs text-slate-500">
                        Vence el {format(parseISO(b.expires_at), "d 'de' MMM yyyy", { locale: es })}
                      </span>
                    )}
                    <Link to={`/agendar?profesional=${b.psychologist_id}`}>
                      <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white rounded-full">
                        Agendar
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Otras horas agendadas */}
      {proximas.length > 1 && (
        <section className="mt-8">
          <h2 className="font-heading text-lg font-semibold text-slate-900 mb-3">
            Tus otras horas
          </h2>
          <div className="space-y-3">
            {proximas.slice(1).map((r) => (
              <ReservaCard key={r.id} reserva={r} onCancelar={() => setPorCancelar(r)} />
            ))}
          </div>
        </section>
      )}

      {/* Historial */}
      {pasadas.length > 0 && (
        <section className="mt-10">
          <h2 className="font-heading text-lg font-semibold text-slate-900 mb-3">Tu historial</h2>
          <div className="space-y-2">
            {pasadas.map((r) => (
              <ReservaCard key={r.id} reserva={r} pasada />
            ))}
          </div>
        </section>
      )}

      {cargando && reservas.length === 0 && (
        <p className="text-slate-500 mt-6">Cargando…</p>
      )}

      {/* Tus datos */}
      <section className="mt-14 pt-8 border-t border-slate-200">
        <h2 className="font-heading text-lg font-semibold text-slate-900 mb-2">Tus datos</h2>
        <p className="text-sm text-slate-600 mb-4 max-w-2xl">
          Puedes borrar tu identidad cuando quieras. Se cancelan tus horas futuras y tu código
          deja de funcionar. Las sesiones ya realizadas quedan en el registro del profesional
          sin ningún vínculo contigo.
        </p>
        <Button
          variant="outline"
          onClick={() => setPorBorrar(true)}
          className="border-red-200 text-red-700 hover:bg-red-50"
          data-testid="borrar-cuenta-btn"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Borrar mi identidad
        </Button>
      </section>

      <AlertDialog open={Boolean(porCancelar)} onOpenChange={() => setPorCancelar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cancelar esta hora?</AlertDialogTitle>
            <AlertDialogDescription>
              {porCancelar?.from_pack
                ? 'La sesión volverá a tu bono y podrás usarla en otra fecha.'
                : 'Se liberará el horario y el profesional recibirá el aviso. Si ya pagaste, escríbenos para coordinar la devolución.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Volver</AlertDialogCancel>
            <AlertDialogAction onClick={cancelar} className="bg-red-600 hover:bg-red-700">
              Sí, cancelar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={porBorrar} onOpenChange={setPorBorrar}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Borrar tu identidad?</AlertDialogTitle>
            <AlertDialogDescription>
              Esto no se puede deshacer. Tu código dejará de servir, se cancelarán tus horas
              futuras y perderás el acceso a tu historial.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Mejor no</AlertDialogCancel>
            <AlertDialogAction onClick={borrarCuenta} className="bg-red-600 hover:bg-red-700">
              Borrar todo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

/** La próxima sesión, con cuenta regresiva en vivo. */
const ProximaSesion = ({ reserva, onCancelar }) => {
  const [faltan, setFaltan] = useState(null);
  const temporizador = useRef(null);

  const fecha = reserva.scheduled_date ? parseISO(reserva.scheduled_date) : null;

  useEffect(() => {
    if (!fecha) return undefined;

    const calcular = () => Math.max(0, Math.floor((fecha.getTime() - Date.now()) / 1000));
    setFaltan(calcular());

    temporizador.current = setInterval(() => setFaltan(calcular()), 1000);
    return () => clearInterval(temporizador.current);
  }, [reserva.scheduled_date]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Card className="border-teal-300 bg-gradient-to-br from-teal-50 to-white overflow-hidden">
      <CardContent className="p-6 md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div className="min-w-0">
            <p className="text-xs font-medium text-teal-700 uppercase tracking-wide mb-2">
              Tu próxima sesión
            </p>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900">
              {reserva.psychologist_name}
            </h2>
            {fecha && (
              <p className="text-slate-600 mt-1 first-letter:uppercase">
                {format(fecha, "EEEE d 'de' MMMM, HH:mm", { locale: es })} hrs ·{' '}
                {reserva.duration_minutes} min
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {reserva.from_pack && <Badge className="bg-teal-100 text-teal-800 border-0">De tu bono</Badge>}
            {reserva.payment_status === 'paid' ? (
              <Badge className="bg-teal-600 text-white border-0">Pagada</Badge>
            ) : (
              <Badge className="bg-amber-100 text-amber-800 border-0">Falta pagar</Badge>
            )}
          </div>
        </div>

        {/* Cuenta regresiva */}
        {faltan !== null && faltan > 0 && (
          <div className="mb-6">
            <p className="text-sm text-slate-500 mb-1">Comienza en</p>
            <p className="font-mono text-3xl md:text-4xl font-semibold text-slate-900 tabular-nums">
              {formatearFaltan(faltan)}
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {reserva.room_is_open ? (
            <a href={reserva.session_url} target="_blank" rel="noopener noreferrer">
              <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8">
                <Video className="w-4 h-4 mr-2" />
                Entrar a la sesión
              </Button>
            </a>
          ) : (
            <a href={reserva.session_url} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="rounded-full">
                <Video className="w-4 h-4 mr-2" />
                Ver la sala
              </Button>
            </a>
          )}

          {reserva.status === 'pending' && reserva.payment_status !== 'paid' && (
            <Link to={`/pago/resultado?reserva=${reserva.id}`}>
              <Button variant="outline" className="rounded-full">Completar el pago</Button>
            </Link>
          )}

          {reserva.can_reschedule && (
            <Link to={`/reprogramar/${reserva.id}`}>
              <Button variant="outline" className="rounded-full">
                <RefreshCw className="w-4 h-4 mr-2" />
                Cambiar hora
              </Button>
            </Link>
          )}

          {reserva.can_cancel && (
            <Button variant="ghost" onClick={onCancelar} className="text-slate-500 hover:text-red-600 rounded-full">
              <X className="w-4 h-4 mr-1.5" />
              Cancelar
            </Button>
          )}
        </div>

        {!reserva.can_cancel && reserva.status !== 'cancelled' && (
          <p className="text-xs text-slate-500 mt-4">
            Ya pasó el plazo para cancelar o cambiar esta hora.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

/** Una hora agendada o pasada, en formato compacto. */
const ReservaCard = ({ reserva, onCancelar, pasada = false }) => {
  const fecha = reserva.scheduled_date ? parseISO(reserva.scheduled_date) : null;

  const estados = {
    pending: { texto: 'Pendiente de pago', clase: 'bg-amber-100 text-amber-800' },
    confirmed: { texto: 'Confirmada', clase: 'bg-teal-100 text-teal-800' },
    completed: { texto: 'Realizada', clase: 'bg-slate-100 text-slate-700' },
    cancelled: { texto: 'Cancelada', clase: 'bg-red-100 text-red-700' },
    no_show: { texto: 'No asististe', clase: 'bg-red-100 text-red-700' },
  };
  const estado = estados[reserva.status] || estados.pending;

  return (
    <Card className={pasada ? 'border-slate-200 bg-slate-50/50' : 'border-slate-200'}>
      <CardContent className="py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="font-medium text-slate-900">{reserva.psychologist_name}</span>
              <Badge className={`${estado.clase} border-0 text-xs`}>{estado.texto}</Badge>
              {reserva.from_pack && (
                <Badge className="bg-teal-50 text-teal-700 border-0 text-xs">Bono</Badge>
              )}
            </div>
            {fecha && (
              <p className="text-sm text-slate-500 first-letter:uppercase">
                {format(fecha, "EEEE d 'de' MMMM, HH:mm", { locale: es })} hrs
                {!reserva.from_pack && ` · ${pesos(reserva.amount)}`}
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {reserva.room_is_open && (
              <a href={reserva.session_url} target="_blank" rel="noopener noreferrer">
                <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white rounded-full">
                  <Video className="w-4 h-4 mr-1.5" />
                  Entrar
                </Button>
              </a>
            )}

            {reserva.review_token && (
              <Link to={`/valorar/${reserva.review_token}`}>
                <Button size="sm" variant="outline" className="rounded-full">
                  <Star className="w-4 h-4 mr-1.5" />
                  Valorar
                </Button>
              </Link>
            )}

            {reserva.can_reschedule && (
              <Link to={`/reprogramar/${reserva.id}`}>
                <Button size="sm" variant="outline" className="rounded-full">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </Link>
            )}

            {reserva.can_cancel && onCancelar && (
              <Button
                size="sm"
                variant="ghost"
                onClick={onCancelar}
                className="text-slate-400 hover:text-red-600 rounded-full"
                data-testid={`cancelar-${reserva.id}`}
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/** Segundos a algo legible: 3 días, 5h 20m, o 04:35. */
function formatearFaltan(segundos) {
  const d = Math.floor(segundos / 86400);
  const h = Math.floor((segundos % 86400) / 3600);
  const m = Math.floor((segundos % 3600) / 60);
  const s = segundos % 60;

  if (d > 0) return `${d} ${d === 1 ? 'día' : 'días'} y ${h}h`;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default MisHorasPage;
